import api from '../../api';

export type EconReportNameSection = {
  ticker: string;
  nameJa: string;
  past: string;
  present: string;
  future: string;
  body: string;
};

export type EconReportCitation = {
  n: number;
  title: string;
  publisher: string;
  date: string;
  url: string;
  raw: string;
};

export type EconReportBody = {
  abstract: string;
  introduction: string;
  results: { trends: string; namesLead: string; names: EconReportNameSection[] };
  conclusion: string;
};

/** 本文を持つ言語。UI は 8 言語だが、レポート本体は日本語と英語の 2 つ */
export type EconReportLang = 'ja' | 'en';

export type EconReportSummary = {
  slug: string;
  /** 実際に返ってきた言語。英語版が無いレポートは 'ja' */
  lang: EconReportLang;
  availableLangs: EconReportLang[];
  title: string;
  keywords: string;
  asOfDate: string | null;
  publishedAt: string | null;
  pageEstimate: number;
  charCount: number;
  citationCount: number;
  nameCount: number;
  lead: string;
};

export type EconReportDetail = EconReportSummary & {
  body: EconReportBody;
  citations: EconReportCitation[];
  disclaimer: string;
  assets: { fileName: string; mimeType: string; captionJa: string | null; sortOrder: number }[];
};

export type EconReportHub = {
  lang: EconReportLang;
  title: string;
  lead: string;
  disclaimer: string;
  reports: EconReportSummary[];
};

export type EconReportJob = {
  id: string;
  phase: string;
  status: string;
  interactionId: string | null;
  errorText: string | null;
  startedAt: string | null;
  finishedAt: string | null;
};

export type EconReportValidation = {
  ok: boolean;
  errors: string[];
  warnings: string[];
  stats: {
    charCount: number;
    pageEstimate: number;
    quantClaims: number;
    citationCount: number;
    nameCount: number;
    missingCitationRefs: number[];
    unusedCitations: number[];
  };
  publishedWithOverride?: boolean;
};

export type EconReportAdmin = EconReportSummary & {
  id: string;
  status: string;
  titleJa: string;
  titleEn: string | null;
  focusNoteJa: string | null;
  planMarkdown: string | null;
  rawMarkdown: string | null;
  rawMarkdownEn: string | null;
  translatedAt: string | null;
  validation: EconReportValidation | Record<string, never> | null;
  validationEn: EconReportValidation | Record<string, never> | null;
  agentId: string | null;
  createdAt: string;
  updatedAt: string;
  latestJob: EconReportJob | null;
};

export type CreateEconReportPayload = {
  titleJa: string;
  keywordsJa: string;
  focusNoteJa?: string;
  slug?: string;
  asOfDate?: string;
  skipPlanning?: boolean;
};

async function unwrap<T>(promise: Promise<{ data: { success: boolean; data: T } }>): Promise<T> {
  const res = await promise;
  return res.data.data;
}

export const econReportService = {
  getHub(lang?: string): Promise<EconReportHub> {
    return unwrap(
      api.get<{ success: boolean; data: EconReportHub }>('/econ-report/public/hub', {
        params: lang ? { lang } : undefined,
      })
    );
  },

  getReport(slug: string, lang?: string): Promise<EconReportDetail> {
    return unwrap(
      api.get<{ success: boolean; data: EconReportDetail }>(
        `/econ-report/public/reports/${encodeURIComponent(slug)}`,
        { params: lang ? { lang } : undefined }
      )
    );
  },

  listAdmin(): Promise<EconReportAdmin[]> {
    return unwrap(
      api.get<{ success: boolean; data: EconReportAdmin[] }>('/econ-report/admin/reports')
    );
  },

  getAdmin(reportId: string): Promise<EconReportAdmin> {
    return unwrap(
      api.get<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}`
      )
    );
  },

  create(payload: CreateEconReportPayload): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>('/econ-report/admin/reports', payload)
    );
  },

  sync(reportId: string): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/sync`
      )
    );
  },

  refinePlan(reportId: string, instruction: string): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/plan/refine`,
        { instruction }
      )
    );
  },

  approvePlan(reportId: string): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/plan/approve`
      )
    );
  },

  translate(reportId: string): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/translate`
      )
    );
  },

  repair(reportId: string): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/repair`
      )
    );
  },

  publish(reportId: string, force = false): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/publish`,
        { force }
      )
    );
  },

  unpublish(reportId: string): Promise<EconReportAdmin> {
    return unwrap(
      api.post<{ success: boolean; data: EconReportAdmin }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}/unpublish`
      )
    );
  },

  remove(reportId: string): Promise<{ deleted: true }> {
    return unwrap(
      api.delete<{ success: boolean; data: { deleted: true } }>(
        `/econ-report/admin/reports/${encodeURIComponent(reportId)}`
      )
    );
  },
};
